import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { AfterViewInit } from "@angular/core";
import { ActivatedRoute, Router } from '@angular/router';
import { JobProcedureService } from 'src/app/services/sections/job/job-procedure.service';
import { JobProcedureCopilotConfirmationDetails, JobProcedureFormPhoto, JobProcedureFormCopilotConfirmationDetails, JobProcedureFormCopilotConfirmation } from "src/app/models/procedure-copilot-confirmation-details.model";
import { TokenHelper } from "src/app/services/helpers/token.helper";
import { GenericModalComponent } from "../generic-modal/generic-modal.component";
import { ToastService } from "src/app/services/ui/toast.service";
import { ToastType } from "../toast/models/toast-type.enum";
import { FormTemplate } from "src/app/models/forms/form-template.model";
import moment from 'moment';

@Component({
  selector: "procedure-copilot-confirmation",
  templateUrl: "./procedure-copilot-confirmation.component.html",
  styleUrls: ["./procedure-copilot-confirmation.component.scss", '../job-details/job-details.component.scss', '../stylesheets/job-actions.scss', '../stylesheets/job-main.scss'],
})
export class ProcedureCopilotConfirmationComponent implements OnInit, AfterViewInit, OnDestroy {
  public readonly self = this;
  public readonly moment = moment;

  @ViewChild('formsModal', { static: true }) public modal: GenericModalComponent;
  @ViewChild('logIssueModal', { static: true }) public logIssueModal: GenericModalComponent;
  public isLoading: boolean;
  public procedureCopilotDetails: JobProcedureCopilotConfirmationDetails;
  public expandedStepIndex: number = -1;
  
  public currentPreviewStepPhotos: JobProcedureFormPhoto[] = [];
  public currentPreviewStepResult: FormTemplate | null = null;
  public isStepPhotosLoading: boolean = false;
  public isStepResultLoading: boolean = false;
  public logIssueComment: string = '';
  public isOnLogIssueUpdate: boolean = false;

  public selectedStepForIssue: JobProcedureFormCopilotConfirmationDetails | null = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private procedureService: JobProcedureService
  ) {
    this.procedureCopilotDetails = this.activatedRoute.snapshot.data.procedureCopilotDetails as JobProcedureCopilotConfirmationDetails;
  }

  get isProcedureCompleted(): boolean {
    return this.procedureCopilotDetails?.pendingApprovalJobProcedureForms?.length > 0 &&
      this.procedureCopilotDetails?.pendingApprovalJobProcedureForms.every(p => p.isConfirmed || p.isIssueLogged);
  }

  get isStepUpdateValid(): boolean {
    return this.selectedStepForIssue !== null &&
      (this.selectedStepForIssue?.isConfirmed || this.selectedStepForIssue?.isIssueLogged);
  }
  ngOnInit(): void {
    this.injectNavBarActions();
  }

  ngAfterViewInit(): void {
    GlobalSettingsService.isGlobalLoaderLoading = false;
    GlobalSettingsService.isSecondaryLoaderLoading = false;
  }

  ngOnDestroy(): void {
    GlobalSettingsService.navigationBarActions.clear();
  }

  public currentPendingApprovalStepId(index: number, step: JobProcedureFormCopilotConfirmationDetails): number {
    return step.jobProcedureFormId;
  }

  private injectNavBarActions(): void {
    GlobalSettingsService.navigationBarActions.set(
      'pending-procedures-access-button',
      {
        icon: 'menu',
        listOrder: 0,
        text: 'Procedure Requests',
        isInDropdownMenu: false,
        isTextOnlyShownInDropdown: true,
        action: () => this.router.navigate(['pending-procedures'], { relativeTo: this.activatedRoute })
      }
    );
  }

  private toggleStepExpansion(currentIndex: number, steps: JobProcedureFormCopilotConfirmationDetails[]): void {
    const nextIndex = steps.findIndex((_, index) => index > currentIndex && steps[index].isConfirmed === false && steps[index].isIssueLogged === false);
    this.expandedStepIndex = this.expandedStepIndex === nextIndex ? -1 : nextIndex;
  }

  public goToFormDetails(step: JobProcedureFormCopilotConfirmationDetails): void {
    if(!step) {
      console.error('No step provided to navigate to form details.');
      return;
    }
    this.router.navigate([`./${step.jobProcedureFormId}/form-details`], {relativeTo: this.activatedRoute,  state:{procedureName:step.stepName, expandedStepIndex:this.expandedStepIndex} });
  }

  public isJobProcedureFormConfirmed(step: JobProcedureFormCopilotConfirmationDetails): void {
    this.isLoading = true;
    this.procedureService.isJobProcedureFormConfirmed(TokenHelper.getToken(this.activatedRoute),step.jobProcedureFormId).subscribe(
      (isConfirmed: boolean) => {
        this.isLoading = false;
        if (isConfirmed) {
          let index = this.procedureCopilotDetails.pendingApprovalJobProcedureForms.findIndex(s => s.jobProcedureFormId === step.jobProcedureFormId);
          if (index !== -1) {
            this.procedureCopilotDetails.pendingApprovalJobProcedureForms[index].isConfirmed = true;
          }
        }
      },
      (error) => {
        this.isLoading = false;
        console.error('Error checking step status', error);
        ToastService.pushToast({
          text: 'Unable to check step status. Please try again!',
          icon: 'error',
          type: ToastType.Error
        });
      }
    );
  }

  public confirmCopilotStep(step: JobProcedureFormCopilotConfirmationDetails): void {
    this.isLoading = true;
    step.isConfirmed = true;
    step.isIssueLogged = false;
    step.comment = null;
    this.procedureService.updateCopilotStep(TokenHelper.getToken(this.activatedRoute), step).subscribe(
      (updatedStep: JobProcedureFormCopilotConfirmationDetails) => {
        this.isLoading = false;
        let index = this.procedureCopilotDetails.pendingApprovalJobProcedureForms.findIndex(s => s.jobProcedureFormId === step.jobProcedureFormId);
        if (index !== -1) {
          this.procedureCopilotDetails.pendingApprovalJobProcedureForms[index] = updatedStep;
        }
        this.toggleStepExpansion(this.expandedStepIndex, this.procedureCopilotDetails.pendingApprovalJobProcedureForms);
        if (this.isProcedureCompleted) {
          this.procedureCopilotDetails.isCompleted = true;
        }
        this.closeLogIssueModal();
      },
      (error) => {
        this.isLoading = false;
        console.error('Error confirming step', error);
        ToastService.pushToast({
          text: 'Unable to confirm step. Please try again!',
          icon: 'error',
          type: ToastType.Error
        });
      }
    );
  }

  public openStepUpdateModal(step: JobProcedureFormCopilotConfirmationDetails): void {
    this.logIssueModal.open();
    this.selectedStepForIssue = step;
    this.logIssueComment = null;
    this.isOnLogIssueUpdate = false;
  }

  public submitLogIssue(): void {
    this.isLoading = true;
    this.selectedStepForIssue.comment = this.logIssueComment;
    this.selectedStepForIssue.isIssueLogged = true;
    this.selectedStepForIssue.isConfirmed = false;
    this.procedureService.updateCopilotStep(TokenHelper.getToken(this.activatedRoute), this.selectedStepForIssue).subscribe(
      (updatedStep: JobProcedureFormCopilotConfirmationDetails) => {
        this.isLoading = false;
        let index = this.procedureCopilotDetails.pendingApprovalJobProcedureForms.findIndex(s => s.jobProcedureFormId === this.selectedStepForIssue.jobProcedureFormId);
        if (index !== -1) {
          this.procedureCopilotDetails.pendingApprovalJobProcedureForms[index] = updatedStep;
        }
        if (this.isProcedureCompleted) {
          this.procedureCopilotDetails.isCompleted = true;
        }
        this.toggleStepExpansion(this.expandedStepIndex, this.procedureCopilotDetails.pendingApprovalJobProcedureForms);
        this.closeLogIssueModal();
      },
      (error) => {
        this.isLoading = false;
        console.error('Error logging issue', error);
        ToastService.pushToast({
          text: 'Unable to log issue. Please try again!',
          icon: 'error',
          type: ToastType.Error
        });
      }
    );
  }

  public closeLogIssueModal(): void {
    this.logIssueModal.hide();
    this.selectedStepForIssue = null;
    this.logIssueComment = '';
    this.isOnLogIssueUpdate = false;
  }

  public onLogIssueClick(): void {
    if (!this.isOnLogIssueUpdate) {
      this.isOnLogIssueUpdate = true;
    } else {
      this.submitLogIssue();
    }
  }

  public getConfirmationHistoryByFormId(jobProcedureFormId: number): void{
    this.isLoading = true;
    this.procedureService.getCopilotConfirmationHistory(TokenHelper.getToken(this.activatedRoute), jobProcedureFormId).subscribe(
      (history: JobProcedureFormCopilotConfirmation[]) => {
        this.updateStepConfirmationHistory(jobProcedureFormId, history);
        this.isLoading = false;
      },
      (error) => {
        console.error('Error fetching confirmation history', error);
        ToastService.pushToast({
          text: 'Unable to fetch confirmation history. Please try again!',
          icon: 'error',
          type: ToastType.Error
        });
        this.isLoading = false;
      }
    );
  }

  private updateStepConfirmationHistory(jobProcedureFormId: number, history: JobProcedureFormCopilotConfirmation[]): void {
    const index = this.procedureCopilotDetails.pendingApprovalJobProcedureForms.findIndex(step => step.jobProcedureFormId === jobProcedureFormId);
    if (index !== -1) {
      this.procedureCopilotDetails.pendingApprovalJobProcedureForms[index].confirmationHistory = history;
    }
  }
}
